import React, { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeSlash, GraduationCap } from "@phosphor-icons/react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const DEMO = [
  { key: "super_admin", email: "admin@bilinguist.com", pw: "Admin@123" },
  { key: "director", email: "director@bilinguist.com", pw: "Director@123" },
  { key: "branch_manager", email: "manager@bilinguist.com", pw: "Manager@123" },
  { key: "secretary", email: "secretary@bilinguist.com", pw: "Secretary@123" },
  { key: "teacher", email: "teacher@bilinguist.com", pw: "Teacher@123" },
  { key: "student", email: "student@bilinguist.com", pw: "Student@123" },
  { key: "parent", email: "parent@bilinguist.com", pw: "Parent@123" },
  { key: "accountant", email: "accountant@bilinguist.com", pw: "Accountant@123" },
];

export default function Login() {
  const { user, login } = useAuth();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email, pw);
    setBusy(false);
    if (!res.ok) toast.error(res.error);
    else toast.success(t("common.welcome"));
  };

  const quickLogin = async (d) => {
    setEmail(d.email); setPw(d.pw); setBusy(true);
    const res = await login(d.email, d.pw);
    setBusy(false);
    if (!res.ok) toast.error(res.error);
  };

  return (
    <div className="min-h-screen w-full flex bg-background">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[46%] bg-primary text-primary-foreground p-12 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-brand-red/20 blur-3xl" />
        <div className="absolute -bottom-32 -left-16 w-96 h-96 rounded-full bg-white/5 blur-3xl" />
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-primary-foreground/10 flex items-center justify-center">
              <GraduationCap size={22} weight="duotone" />
            </div>
            <div>
              <div className="font-bold text-xl" style={{ fontFamily: "Manrope" }}>Bilinguist</div>
              <div className="text-[10px] uppercase tracking-[0.22em] opacity-70">School OS</div>
            </div>
          </div>
        </div>
        <div className="relative z-10 space-y-6">
          <div className="inline-block px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-[0.2em] bg-brand-red">Enterprise Suite</div>
          <h1 className="text-4xl xl:text-5xl font-extrabold leading-[1.05]" style={{ fontFamily: "Manrope" }}>
            Run your language school like a<br />
            <span className="brand-red">world-class</span> institution.
          </h1>
          <p className="text-primary-foreground/70 text-base max-w-md leading-relaxed">
            Enrollments, classes, teachers, attendance, payments and analytics — unified in one premium workspace.
          </p>
          <div className="flex items-center gap-6 pt-4 border-t border-primary-foreground/10">
            <div>
              <div className="text-2xl font-bold font-mono">28+</div>
              <div className="text-[10px] uppercase tracking-widest opacity-60">Students</div>
            </div>
            <div>
              <div className="text-2xl font-bold font-mono">8</div>
              <div className="text-[10px] uppercase tracking-widest opacity-60">Roles</div>
            </div>
            <div>
              <div className="text-2xl font-bold font-mono">24/7</div>
              <div className="text-[10px] uppercase tracking-widest opacity-60">Access</div>
            </div>
          </div>
        </div>
        <div className="relative z-10 text-xs opacity-60">© 2026 Bilinguist. All rights reserved.</div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 relative">
        <div className="absolute top-4 right-4"><LanguageSwitcher /></div>
        <div className="w-full max-w-md space-y-8">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">{t("auth.signInTitle")}</div>
            <h2 className="text-3xl font-bold" style={{ fontFamily: "Manrope" }}>{t("auth.signIn")}</h2>
            <p className="text-sm text-muted-foreground mt-2">{t("auth.signInSubtitle")}</p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider">{t("auth.email")}</Label>
              <Input
                id="email" data-testid="login-email" type="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder={t("auth.emailPlaceholder")} className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label htmlFor="pw" className="text-xs font-semibold uppercase tracking-wider">{t("auth.password")}</Label>
              <div className="relative mt-1.5">
                <Input
                  id="pw" data-testid="login-password" type={show ? "text" : "password"} required
                  value={pw} onChange={(e) => setPw(e.target.value)}
                  placeholder="••••••••" className="h-11 pr-10"
                />
                <button
                  type="button" onClick={() => setShow((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {show ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <Button
              data-testid="login-submit" type="submit" disabled={busy}
              className="w-full h-11 rounded-full font-semibold"
            >
              {busy ? t("auth.signingIn") : t("auth.signInBtn")}
            </Button>
            <div className="flex items-center justify-between text-xs pt-1">
              <Link to="/forgot-password" data-testid="forgot-link" className="text-muted-foreground hover:text-primary transition-colors">
                {t("auth.forgotPassword")}
              </Link>
              <Link to="/register" data-testid="register-link" className="font-semibold text-primary hover:underline">
                {t("auth.dontHaveAccount")}
              </Link>
            </div>
          </form>

          <div className="space-y-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{t("auth.demoAccounts")}</div>
            <div className="grid grid-cols-2 gap-2">
              {DEMO.map((d) => (
                <button
                  key={d.key} type="button" onClick={() => quickLogin(d)}
                  data-testid={`demo-${d.key.replace(/_/g, "-")}`}
                  className="text-left px-3 py-2 rounded-md border border-border hover:border-foreground/40 hover:bg-secondary transition-colors"
                >
                  <div className="text-xs font-semibold">{t(`roles.${d.key}`)}</div>
                  <div className="text-[10px] text-muted-foreground truncate">{d.email}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
